import { CollectionRefreshBar } from "@packages/core-client/collection/CollectionRefreshBar.tsx";
import { CollectionRefreshRegion } from "@packages/core-client/collection/CollectionRefreshRegion.tsx";
import { LoadMoreFooter } from "@packages/core-client/collection/LoadMoreFooter.tsx";
import { RefreshErrorBanner } from "@packages/core-client/collection/RefreshErrorBanner.tsx";
import { useInfiniteCollection } from "@packages/core-client/collection/useInfiniteCollection.ts";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { useDebouncedValue } from "@packages/core-client/hooks/useDebouncedValue.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { Collection, ListBoxLoadMoreItem, type Selection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import type { ReviewCycleOption } from "@domains/reviews/shared/reviewee-filters/filterOptionsContract.ts";
import { ListBox, ListBoxDescription, ListBoxItem, ListBoxLabel } from "@/components/ui/list-box.tsx";
import { Note } from "@/components/ui/note.tsx";
import { SearchField, SearchInput } from "@/components/ui/search-field.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Text } from "@/components/ui/text.tsx";

import { i18NextInstanceKey } from "../i18n/i18next.ts";
import { useFilterOptionsClient } from "@domains/reviews/client/api/filterOptionsQuery.ts";
import { FilterPanelBody } from "@domains/reviews/client/components/reviewee-filters/FilterPanelBody.tsx";
import { FilterPanelHeader } from "@domains/reviews/client/components/reviewee-filters/FilterPanelHeader.tsx";
import { ALL_CYCLES_ID, resolveCycleSelection, toSelectedKeys } from "./resolveCycleSelection.ts";

const SEARCH_DEBOUNCE_MS = 200;

// intent-ui-deviation: check-indicator moved to the row end -- inline in column 1 it renders only while selected,
const CHECK_AT_ROW_END = "*:data-[slot=check-indicator]:me-0 *:data-[slot=check-indicator]:mt-0 *:data-[slot=check-indicator]:inset-e-2 *:data-[slot=check-indicator]:absolute *:data-[slot=check-indicator]:top-1/2 *:data-[slot=check-indicator]:-translate-y-1/2";

const PLACEHOLDER_CYCLES: ReviewCycleOption[] = [
    { id: "skeleton-1", label: "Xxxxxxx Xxxxx Xxxxxx", activity: "Locked" },
    { id: "skeleton-2", label: "Xxxx Xxxxxxxx Xxxxxx", activity: "Locked" },
    { id: "skeleton-3", label: "Xxxxxxx Xxxxxx", activity: "Locked" },
    { id: "skeleton-4", label: "Xxxxxxxxx Xxxx Xxxxx", activity: "Locked" }
];

export interface CycleFilterListProps {
    selectedIds: string[];
    isWholeHistory?: boolean;
    onSelectionChange: (reviewCycleIds: string[]) => void;
    mode?: "period" | "breakdown";
    onBack?: () => void;
    maxSelections?: number;
}

export function CycleFilterList({ selectedIds, isWholeHistory = false, onSelectionChange, mode = "period", onBack, maxSelections }: CycleFilterListProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("CycleFilterList", { i18n: i18nextInstance });
    const client = useFilterOptionsClient();
    const language = i18nextInstance.language;

    const [query, setQuery] = useState("");
    const [idsSelectedOnOpen] = useState(selectedIds);
    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

    const { items, isLoading, isEmpty, hasContent, hasActiveFilters, isColdError, isStaleError, isRefreshing, hasNextPage, isFetchingNextPage, loadMore, loadedAnnouncement, refreshedAnnouncement } = useInfiniteCollection<ReviewCycleOption>({
        queryKey: [...client.searchReviewCycles.key(), { q: debouncedQuery, language }],
        queryFn: continuationToken => client.searchReviewCycles.call({ q: debouncedQuery, continuationToken }),
        placeholder: PLACEHOLDER_CYCLES,
        hasActiveFilters: debouncedQuery.trim() !== "",
        getLoadedMoreMessage: count => t("liveRegion.loadedMore", { count }),
        getRefreshedMessage: count => t("liveRegion.results", { count }),
        onError: "inline"
    });

    const isBreakdown = mode === "breakdown";
    const isAtCap = maxSelections !== undefined && selectedIds.length >= maxSelections;

    const handleSelectionChange = useCallback(
        (keys: Selection) => {
            const next = resolveCycleSelection(keys, selectedIds);

            onSelectionChange(maxSelections === undefined ? next : next.slice(0, maxSelections));
        },
        [onSelectionChange, selectedIds, maxSelections]
    );

    const resolveLabel = useCallback((cycle: ReviewCycleOption) => cycle.label || t("untitled"), [t]);
    const resolveActivityLabel = useCallback((cycle: ReviewCycleOption) => (cycle.activity === "Locked" ? t("activity.locked") : t("activity.active")), [t]);

    const idsToResolve = idsSelectedOnOpen.filter(id => !items.some(cycle => cycle.id === id));
    const selectedIdsToResolve = !isLoading && !hasActiveFilters && idsToResolve.length > 0 ? idsToResolve : undefined;

    const selectedCyclesOptions = client.searchReviewCycles.queryOptions({ input: { restrictToReviewCycleIds: selectedIdsToResolve, pageSize: selectedIdsToResolve?.length ?? 1 } });
    const { data: selectedCyclesPage } = useQuery(
        createQueryOptions(selectedCyclesOptions, {
            queryKey: [...selectedCyclesOptions.queryKey, { language }],
            enabled: Boolean(selectedIdsToResolve),
            throwOnError: false
        })
    );

    const rows = useMemo(() => {
        const idSet = new Set(idsSelectedOnOpen);
        const known = [...(selectedCyclesPage?.items ?? []), ...items];
        const selectedRows = idsSelectedOnOpen.flatMap(id => known.find(cycle => cycle.id === id) ?? []);
        const unselectedRows = items.filter(cycle => !idSet.has(cycle.id));

        return [...selectedRows, ...unselectedRows];
    }, [items, selectedCyclesPage, idsSelectedOnOpen]);

    return (
        <FilterPanelBody className={isBreakdown ? "w-72" : undefined}>
            {isBreakdown && <FilterPanelHeader title={t("title")} onBack={onBack} backLabel={t("back")} />}
            <SearchField aria-label={t("searchPlaceholder")} value={query} onChange={setQuery}>
                <SearchInput placeholder={t("searchPlaceholder")} />
            </SearchField>
            <output className="sr-only">{isLoading ? t("loading") : ""}</output>
            <output className="sr-only">{loadedAnnouncement}</output>
            <output className="sr-only">{refreshedAnnouncement}</output>
            {isColdError ? (
                <Note intent="danger" role="alert">
                    {t("error")}
                </Note>
            ) : isEmpty ? (
                <Text>{hasActiveFilters ? t("noResults") : t("empty")}</Text>
            ) : (
                hasContent && (
                    <>
                        {isStaleError && <RefreshErrorBanner message={t("staleError")} className="mb-0" />}
                        <div className="relative flex min-h-0 shrink flex-col">
                            <CollectionRefreshBar isRefreshing={isRefreshing} />
                            <CollectionRefreshRegion isRefreshing={isRefreshing} className="flex min-h-0 shrink flex-col">
                                <Skeleton isLoading={isLoading} inert={isLoading} className="flex min-h-0 shrink flex-col">
                                    <ListBox aria-label={t("title")} selectionMode="multiple" selectedKeys={toSelectedKeys(selectedIds, isWholeHistory)} onSelectionChange={handleSelectionChange} escapeKeyBehavior="none" className="min-h-0 border-0">
                                        {!isLoading && !hasActiveFilters && !isBreakdown && (
                                            <ListBoxItem id={ALL_CYCLES_ID} textValue={t("allCycles")} className={CHECK_AT_ROW_END}>
                                                <ListBoxLabel className="pe-8 font-medium">{t("allCycles")}</ListBoxLabel>
                                            </ListBoxItem>
                                        )}
                                        <Collection items={rows} dependencies={[t]}>
                                            {cycle => (
                                                <ListBoxItem id={cycle.id} textValue={`${resolveLabel(cycle)} ${resolveActivityLabel(cycle)}`} className={CHECK_AT_ROW_END} isDisabled={isAtCap && !selectedIds.includes(cycle.id)}>
                                                    <ListBoxLabel className="truncate pe-24">{resolveLabel(cycle)}</ListBoxLabel>
                                                    <ListBoxDescription className="text-muted-fg absolute inset-e-8 top-2.5 text-xs">{resolveActivityLabel(cycle)}</ListBoxDescription>
                                                </ListBoxItem>
                                            )}
                                        </Collection>
                                        {hasNextPage && !isLoading && (
                                            <ListBoxLoadMoreItem onLoadMore={loadMore} isLoading={isFetchingNextPage}>
                                                <LoadMoreFooter />
                                            </ListBoxLoadMoreItem>
                                        )}
                                    </ListBox>
                                </Skeleton>
                            </CollectionRefreshRegion>
                        </div>
                    </>
                )
            )}
        </FilterPanelBody>
    );
}
